/**
 * Offers Types
 * TypeScript types mirroring the current_offers database table
 *
 * Real DB columns:
 * - offer_assignment_id (uuid, PK)
 * - ref_user_id (bigint)
 * - reward_id (uuid, FK)
 * - display_priority (integer)
 * - assignment_strategy (varchar) - MIGRATION, MANUAL
 * - context_json (jsonb)
 * - created_at (timestamp)
 * - updated_at (timestamp)
 * - partner_product_id (bigint, FK)
 */

// Assignment strategy types - includes all values found in database
export type AssignmentStrategy = 'MIGRATION' | 'MANUAL' | 'AUTO_DEFAULT';

// Context JSON structures per assignment strategy
export interface MigrationContext {
  migration_source: string;
  original_offer_id: string;
  migrated_at: string;
  // Optional override fields (can appear in any strategy)
  available_override?: boolean;
  override_reason?: string;
  show_message_on_card?: boolean;
}

export interface ManualContext {
  assigned_by: string;
  reason: string;
  ticket_id?: string;
  // Optional override fields (can appear in any strategy)
  available_override?: boolean;
  override_reason?: string;
  show_message_on_card?: boolean;
}

export interface AutoDefaultContext {
  source: string;
  reward_key: string;
  reward_variant: string;
  assigned_at: string;
  // Optional override fields (can appear in any strategy)
  available_override?: boolean;
  override_reason?: string;
  show_message_on_card?: boolean;
}

// Union type for all context types
export type OfferContext =
  | MigrationContext
  | ManualContext
  | AutoDefaultContext
  | Record<string, unknown>;

// Main offer response interface - matches real DB schema
export interface OfferResponse {
  offer_id: string; // offer_assignment_id in DB
  user_id: string; // ref_user_id in DB (as string for display)
  reward_id: string;
  partner_product_id: number;
  display_priority: number;
  assignment_strategy: AssignmentStrategy;
  context_json: OfferContext | null;
  created_at: string;
  updated_at: string;
  // Denormalized fields for display (populated from joins)
  reward_name?: string;
  reward_variant?: string;
  provider_name?: string;
  partner_name?: string;
}

// Query parameters for filtering offers
export interface OfferQueryParams {
  user_id?: string;
  reward_id?: string;
  partner_product_id?: number;
  assignment_strategy?: AssignmentStrategy;
  group_by?: 'user_id'; // Group by user_id for user-based pagination
  limit?: number;
  offset?: number;
}

// Request types for CRUD operations
export interface CreateOfferRequest {
  user_id: string;
  reward_id: string;
  partner_product_id: number;
  assignment_strategy: AssignmentStrategy;
  context_json: OfferContext | null;
  display_priority: number;
}

export interface UpdateOfferRequest {
  // Only these fields can be updated
  display_priority?: number;
  assignment_strategy?: AssignmentStrategy;
  context_json?: OfferContext | null;
}

// Helper function to get strategy label
export function getStrategyLabel(strategy: AssignmentStrategy): string {
  const labels: Record<AssignmentStrategy, string> = {
    MIGRATION: 'Migration',
    MANUAL: 'Manual',
    AUTO_DEFAULT: 'Auto Default',
  };
  return labels[strategy] || strategy;
}

// Strategy options for dropdowns
export const ASSIGNMENT_STRATEGY_OPTIONS = [
  { value: 'MIGRATION', label: 'Migration' },
  { value: 'MANUAL', label: 'Manual' },
  { value: 'AUTO_DEFAULT', label: 'Auto Default' },
] as const;
